// src/pages/YearRollover.jsx
// 年度の切り替え【2026-11-03】
//   新しい年度を始めるときの手順を1画面にまとめる。
//     1. 新しい年度の名簿を取り込む（授業と学生ができる。時間割は空）
//     2. 前の年度の同じ授業から、時間割と設定を写す（この画面でまとめて）
//     3. 授業の学期の登録がずれていないか確かめる【2026-11-04】
//     4. （教務）前の年度を締める
//   GET /api/schedule/year-rollover?year= / POST /api/schedule/year-rollover/copy
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import Notice, { toast } from '../components/Notice';
import { confirmDialog } from '../components/ConfirmDialog';
import TermCheckCard from '../components/TermCheckCard';

const auth = () => ({ Authorization: 'Bearer ' + localStorage.getItem('token'), 'Content-Type': 'application/json' });
const thisYear = () => { const d = new Date(); return d.getMonth() + 1 >= 4 ? d.getFullYear() : d.getFullYear() - 1; };

export default function YearRollover({ user }) {
  const isStaff = user && (user.role === 'academic_staff' || user.role === 'developer');
  // 2月〜3月は来年度の準備をすることが多いので、来年度を先に出す
  const [year, setYear] = useState(() => { const m = new Date().getMonth() + 1; return (m === 2 || m === 3) ? thisYear() + 1 : thisYear(); });
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');
  const [picks, setPicks] = useState({});     // 授業の id → 写し元の id（'' は写さない）
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setErr(''); setData(null);
    try {
      const r = await fetch('/api/schedule/year-rollover?year=' + year, { headers: auth() });
      const j = await r.json().catch(() => ({}));
      if (!r.ok || !j.success) throw new Error(j.message || '読み込めませんでした');
      setData(j);
      const p = {};
      for (const c of j.classes) if (c.scheduleCount === 0 && c.suggested) p[c.id] = String(c.suggested.id);
      setPicks(p);
    } catch (e) { setErr(e.message); }
  }, [year]);
  useEffect(() => { load(); }, [load]);

  const empty = useMemo(() => (data ? data.classes.filter(c => c.scheduleCount === 0) : []), [data]);
  const ready = useMemo(() => empty.filter(c => picks[c.id]), [empty, picks]);

  const copyAll = async () => {
    if (!ready.length) return;
    const okGo = await confirmDialog(
      '選んだ前の年度の授業から、曜日時限・受付・遅刻の線・自動出席・教室を写します。学生・出席は写しません。あとから各授業の授業スケジュールで直せます。',
      { title: `${ready.length}授業に時間割を写します`, okLabel: '写す', danger: false });
    if (!okGo) return;
    setBusy(true);
    try {
      const r = await fetch('/api/schedule/year-rollover/copy', {
        method: 'POST', headers: auth(),
        body: JSON.stringify({ pairs: ready.map(c => ({ targetClassId: c.id, sourceClassId: Number(picks[c.id]) })) }),
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok || !j.success) throw new Error(j.message || '写せませんでした');
      toast.success(j.message);
      await load();
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  return (
    <div className="page-container yr-page">
      <div className="page-header">
        <h1>年度の切り替え</h1>
        <p>新しい年度の授業を始める準備です。上から順に進めてください。</p>
      </div>

      <div className="yr-year">
        <label>年度：</label>
        {[thisYear() - 1, thisYear(), thisYear() + 1].map(y => (
          <button key={y} type="button" className={'npf-seg-btn' + (y === year ? ' on' : '')} onClick={() => setYear(y)}>{y}年度</button>
        ))}
      </div>

      <ol className="yr-steps">
        <li className="card yr-step">
          <div className="yr-step-h"><span className="yr-no">1</span><h2>新しい年度の名簿を取り込む</h2></div>
          <p>manaba や教務の受講者一覧を取り込むと、{year}年度の授業と学生ができます（時間割はまだ空です）。</p>
          <div className="yr-actions">
            <Link className="btn-secondary yr-btn" to={isStaff ? '/students' : '/classes'}>{isStaff ? '学生管理で取り込む' : '授業管理で取り込む'}</Link>
            <Link className="btn-secondary yr-btn" to="/roster-imports">取り込みの履歴</Link>
          </div>
          {data && <p className="yr-note">いま {year}年度の授業は {data.counts.total}件あります。</p>}
        </li>

        <li className="card yr-step">
          <div className="yr-step-h"><span className="yr-no">2</span><h2>前の年度の授業から時間割を写す</h2></div>
          <Notice kind="error">{err}</Notice>
          {!data && !err && <p style={{ color: '#6b7280' }}>読み込み中…</p>}
          {data && empty.length === 0 && (
            <p style={{ color: '#15803d', fontWeight: 600 }}>
              {data.counts.total ? `${year}年度の授業は、どれも曜日時限が入っています。` : `${year}年度の授業はまだありません。先に名簿を取り込んでください。`}
            </p>
          )}
          {empty.length > 0 && (
            <>
              <p>曜日時限が入っていない授業が <b>{empty.length}件</b> あります。前の年度に同じ名前の授業があれば、写し元を選んであります。</p>
              <div className="table-wrap">
                <table className="data-table yr-table">
                  <thead><tr><th>{year}年度の授業</th>{isStaff && <th>先生</th>}<th>履修者</th><th>写し元（前の年度）</th></tr></thead>
                  <tbody>
                    {empty.map(c => (
                      <tr key={c.id}>
                        <td><Link to={`/classes/${c.id}/schedule`}>{c.name}</Link>{c.closed ? <small style={{ color: '#b45309' }}>（締め済み）</small> : null}</td>
                        {isStaff && <td>{c.teacherName || '—'}</td>}
                        <td>{c.enrolled}人</td>
                        <td>
                          {c.candidates.length === 0 ? <span style={{ color: '#9ca3af' }}>候補なし（授業スケジュールで入れる）</span> : (
                            <select value={picks[c.id] || ''} onChange={e => setPicks(p => ({ ...p, [c.id]: e.target.value }))} disabled={busy || c.closed}>
                              <option value="">写さない</option>
                              {c.candidates.map(s => (
                                <option key={s.id} value={s.id}>
                                  {s.academicYear ? `${s.academicYear}年度 ` : ''}{s.name}（{s.scheduleCount}件）{s.match === 'same' ? ' ★' : ''}
                                </option>
                              ))}
                            </select>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="yr-actions">
                <button type="button" className="btn-primary" disabled={busy || ready.length === 0} onClick={copyAll}>
                  {busy ? '写しています…' : `選んだ ${ready.length}授業に写す`}
                </button>
                <span className="yr-note">★ は前の年度に同じ名前の授業があるもの。学生・出席は写しません。</span>
              </div>
            </>
          )}
        </li>

        <TermCheckCard year={year} isStaff={isStaff} no={3} />

        {isStaff && (
          <li className="card yr-step">
            <div className="yr-step-h"><span className="yr-no">4</span><h2>前の年度を締める</h2></div>
            <p>{year - 1}年度の出席がそろったら、年度を締めて読み取り専用にします（あとから再開もできます）。</p>
            <div className="yr-actions"><Link className="btn-secondary yr-btn" to="/data-retention">年度の締め・保管へ</Link></div>
          </li>
        )}
      </ol>
    </div>
  );
}
